import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, useReducedMotion } from "framer-motion";
import { BarChart3, EyeOff, FileLock2, LogOut, Maximize2, Minimize2, ShieldCheck, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useConsent } from "../../lib/consentContext";
import { track } from "../../lib/analytics";
import { logoutInvestor } from "./investorApi";
import { InvestorModelProvider } from "./InvestorModelContext";
import { InvestorScene } from "./InvestorScene";
import { InvestorTrack } from "./InvestorTrack";
import { InvestorVfx } from "./InvestorVfx";
import { FinancialLab } from "./FinancialLab";
import { DataRoom } from "./DataRoom";

const PARTS = ["THESIS", "PRODUCT", "GROWTH", "ECONOMICS"];
const partFor = (chapter) => PARTS[chapter <= 5 ? 0 : chapter <= 14 ? 1 : chapter <= 20 ? 2 : 3];

const PitchExperience = ({ data, onLogout }) => {
  const { openPreferences } = useConsent();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const initialIndex = Math.max(0, data.chapters.findIndex((item) => item.slug === location.pathname.split("/")[2]));
  const [active, setActive] = useState(initialIndex);
  const [direction, setDirection] = useState(1);
  const [presenting, setPresenting] = useState(false);
  const [vfxEnabled, setVfxEnabled] = useState(true);
  const [labOpen, setLabOpen] = useState(false);
  const [roomOpen, setRoomOpen] = useState(false);
  const touchStart = useRef(null);
  const wheelLocked = useRef(false);

  const goTo = useCallback((index) => {
    const bounded = Math.max(0, Math.min(data.chapters.length - 1, index));
    if (bounded === active) return;
    setDirection(bounded > active ? 1 : -1);
    setActive(bounded);
    window.history.replaceState({}, "", `/invest/${data.chapters[bounded].slug}`);
  }, [active, data.chapters]);

  useEffect(() => {
    const slug = location.pathname.split("/")[2];
    const index = data.chapters.findIndex((item) => item.slug === slug);
    if (index >= 0 && index !== active) { setDirection(index > active ? 1 : -1); setActive(index); }
  }, [active, data.chapters, location.pathname]);

  useEffect(() => { track("invest_chapter_viewed", { chapter: active + 1 }); }, [active]);

  const togglePresentation = useCallback(async () => {
    if (!presenting) {
      setPresenting(true); document.documentElement.classList.add("invest-presenting");
      try { await document.documentElement.requestFullscreen?.(); } catch {}
      track("invest_presentation_started");
    } else {
      setPresenting(false); document.documentElement.classList.remove("invest-presenting");
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch {}
    }
  }, [presenting]);

  useEffect(() => {
    const onWheel = (event) => {
      if (labOpen || roomOpen || event.target.closest(".financial-lab,.data-room,.unit-inputs,.competitive-table,.source-ledger,.roadmap-rail")) return;
      if (Math.max(Math.abs(event.deltaX), Math.abs(event.deltaY)) < 24) return;
      event.preventDefault();
      if (wheelLocked.current) return;
      wheelLocked.current = true;
      const amount = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      goTo(active + (amount > 0 ? 1 : -1));
      window.setTimeout(() => { wheelLocked.current = false; }, 850);
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [active, goTo, labOpen, roomOpen]);

  useEffect(() => {
    const onKey = (event) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName) || labOpen || roomOpen) return;
      if (event.key.toLowerCase() === "p") { event.preventDefault(); togglePresentation(); }
      else if (["ArrowRight", "ArrowDown", " "].includes(event.key)) { event.preventDefault(); goTo(active + 1); }
      else if (["ArrowLeft", "ArrowUp"].includes(event.key)) { event.preventDefault(); goTo(active - 1); }
      else if (event.key === "Home") { event.preventDefault(); goTo(0); }
      else if (event.key === "End") { event.preventDefault(); goTo(data.chapters.length - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, data.chapters.length, goTo, labOpen, roomOpen, togglePresentation]);

  useEffect(() => {
    const syncFullscreen = () => { if (!document.fullscreenElement) { setPresenting(false); document.documentElement.classList.remove("invest-presenting"); } };
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  const logout = async () => { await logoutInvestor().catch(() => {}); onLogout(); };
  const finishTouch = (event) => {
    if (!touchStart.current || touchStart.current.interactive) { touchStart.current = null; return; }
    const touch = event.changedTouches?.[0];
    if (!touch) { touchStart.current = null; return; }
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    const distance = Math.abs(dx) > Math.abs(dy) ? dx : dy;
    if (Math.abs(distance) > 55) goTo(active + (distance < 0 ? 1 : -1));
    touchStart.current = null;
  };

  return <main className={`invest-site invest-cinematic ${presenting ? "is-presenting" : ""}`} onTouchStart={(event) => { const touch = event.touches?.[0]; if (!touch) return; touchStart.current = { x: touch.clientX, y: touch.clientY, interactive: Boolean(event.target.closest("button,input,a,textarea,select")) }; }} onTouchEnd={finishTouch} data-testid="investor-pitch-site">
    <InvestorVfx active={active} enabled={vfxEnabled} reducedMotion={prefersReducedMotion} />
    <header className="invest-topbar"><img src="/brand/humn-wordmark.png" alt="HUMN"/><span data-testid="invest-current-part"><ShieldCheck/> PART {active <= 4 ? "I" : active <= 13 ? "II" : active <= 19 ? "III" : "IV"} · {partFor(active + 1)}</span><div><button type="button" onClick={() => setVfxEnabled((value) => !value)} aria-pressed={vfxEnabled} title={vfxEnabled ? "Disable visual effects" : "Enable visual effects"} data-testid="invest-vfx-toggle">{vfxEnabled ? <Sparkles/> : <EyeOff/>}<span>VFX</span></button><button type="button" onClick={() => setLabOpen(true)} data-testid="invest-open-financial-lab"><BarChart3/><span>Financial Model</span></button><button type="button" onClick={() => setRoomOpen(true)} data-testid="invest-open-data-room"><FileLock2/><span>Data Room</span></button><button type="button" onClick={(event) => openPreferences(event.currentTarget)} data-testid="invest-cookie-preferences">Privacy</button><button type="button" onClick={togglePresentation} data-testid="invest-presentation-mode">{presenting ? <Minimize2/> : <Maximize2/>}<span>{presenting ? "Exit" : "Present"}</span></button><button type="button" onClick={logout} aria-label="Log out investor session" data-testid="invest-logout"><LogOut/></button></div></header>
    <div className="invest-chapters" data-testid="invest-spatial-stage">
      <AnimatePresence initial={false} mode="sync">
        <InvestorScene key={data.chapters[active].slug} chapter={data.chapters[active]} data={data} onNext={() => goTo(active + 1)} direction={direction} reducedMotion={prefersReducedMotion} />
      </AnimatePresence>
    </div>
    <InvestorTrack chapters={data.chapters} active={active} onSelect={goTo}/>
    <FinancialLab data={data} open={labOpen} setOpen={setLabOpen}/><DataRoom data={data} open={roomOpen} setOpen={setRoomOpen}/>
  </main>;
};

export const InvestorShell = ({ data, onLogout }) => <InvestorModelProvider data={data}><PitchExperience data={data} onLogout={onLogout}/></InvestorModelProvider>;